import { useEffect, useState } from "react";
import { useAssetLibrary } from "../api/assetlib";
import { ApiError } from "../api/client";
import type { AssetItem } from "../api/types";
import { messageFor } from "../copy/errors";
import { Button, Dialog, Input, Skeleton } from "../ui";

export const PICKER_GRID =
  "m-0 grid list-none grid-cols-2 gap-3 p-0 sm:grid-cols-3";
const SKELETONS = 8;

/** The cards a picker shows while its list loads. */
export function PickerSkeletons() {
  return (
    <ul className={PICKER_GRID} aria-busy="true">
      {Array.from({ length: SKELETONS }).map((_, i) => (
        <li key={i} data-testid="picker-skeleton">
          <span className="block aspect-square w-full overflow-hidden rounded-lg">
            <Skeleton width="100%" height="100%" />
          </span>
          <span className="mt-2 block">
            <Skeleton width="70%" height={14} />
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Every page of one kind of the 3D library, for a picker that filters in
 *  the browser. */
export function useWholeLibrary(kind: "material" | "skin") {
  const library = useAssetLibrary({ kind, limit: 60 });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = library;
  // A further page that failed is a failure of the list, not a reason to ask
  // again and again.
  const failed = library.isError || library.isFetchNextPageError;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !failed) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, failed, fetchNextPage]);
  return {
    // Still loading while a further page is on its way: a list that grows
    // under the pointer is worse than one that waits.
    loading: library.isPending || Boolean(hasNextPage),
    error: failed ? library.error : null,
    items: library.data?.pages.flatMap((page) => page.items) ?? [],
  };
}

function Surfaces(props: {
  accepts: string[];
  onChoose(item: AssetItem): void;
}) {
  const library = useWholeLibrary("material");
  const [search, setSearch] = useState("");
  const needle = search.trim().toLowerCase();
  const offered = library.items.filter(
    (item) =>
      !item.stale &&
      item.surface !== undefined &&
      props.accepts.includes(item.surface),
  );
  const shown = needle
    ? offered.filter(
        (item) =>
          item.name.toLowerCase().includes(needle) ||
          item.tags.some((tag) => tag.toLowerCase().includes(needle)),
      )
    : offered;
  return (
    <div className="flex flex-col gap-4">
      <Input
        type="search"
        aria-label="Search surfaces"
        placeholder="Search"
        autoFocus
        className="min-h-[44px] sm:min-h-0"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      {library.error ? (
        <p role="alert" className="m-0 text-sm text-slate-700">
          {messageFor(
            library.error instanceof ApiError ? library.error.code : undefined,
          )}
        </p>
      ) : library.loading ? (
        <PickerSkeletons />
      ) : shown.length === 0 ? (
        <p className="m-0 text-sm text-slate-700">No surface matches.</p>
      ) : (
        <ul className={PICKER_GRID}>
          {shown.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className="block w-full rounded-lg border-0 bg-transparent p-0 text-left"
                onClick={() => props.onChoose(item)}
              >
                <span className="block aspect-square w-full overflow-hidden rounded-lg bg-slate-100">
                  {item.thumb_url ? (
                    <img
                      src={item.thumb_url}
                      alt=""
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  ) : null}
                </span>
                <span className="mt-2 block break-words text-sm font-semibold text-slate-800">
                  {item.name}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** "Choose a surface": the surfaces whose kind the selected surface of an
 *  object accepts. It never offers one the object would refuse, so the
 *  server's check is a second line, not the first. */
export function SurfacePicker(props: {
  open: boolean;
  onClose(): void;
  /** The surface classes the object's surface accepts. */
  accepts: string[];
  /** Then the caller closes it. */
  onChoose(item: AssetItem): void;
}): JSX.Element {
  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      title="Choose a surface"
      footer={
        <Button type="button" variant="ghost" onClick={props.onClose}>
          Cancel
        </Button>
      }
    >
      <Surfaces accepts={props.accepts} onChoose={props.onChoose} />
    </Dialog>
  );
}
